import { ArrowUp, Mail, MessageCircle } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { CONTACT, LOGO } from "../data/site";
import { useContent } from "../i18n/content";
import { scrollToSection } from "./Navbar";

export const Footer = () => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { ui, site, companies } = useContent();

    const go = (id) => scrollToSection(id, navigate, pathname);

    return (
        <footer data-testid="main-footer" className="bg-[#1e1f1b] border-t border-[#4a4c42]">
            <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-20">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
                    <div>
                        <div className="flex items-center gap-3 mb-6">
                            <img
                                src={LOGO}
                                alt="Sadja Core Corp logo"
                                className="logo-blend h-12 w-12 object-cover rounded-full"
                            />
                            <span className="font-display font-extrabold tracking-[0.18em] text-white">
                                SADJA <span className="text-[#f6c422]">CORE CORP</span>
                            </span>
                        </div>
                        <p className="text-[#a2a59d] text-sm leading-relaxed max-w-xs">
                            {ui.footer.tagline}
                        </p>
                        <p className="overline-text mt-4">{ui.footer.vision}</p>
                    </div>

                    <div>
                        <h4 className="overline-text mb-6">{ui.footer.navigate}</h4>
                        <ul className="space-y-3">
                            {site.navLinks.filter((l) => l.id !== "home").map((link) => (
                                <li key={link.id}>
                                    <button
                                        data-testid={`footer-link-${link.id}`}
                                        onClick={() => go(link.id)}
                                        className="text-sm text-[#a2a59d] hover:text-[#f6c422] transition-colors duration-300"
                                    >
                                        {link.label}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <h4 className="overline-text mb-6">{ui.footer.companies}</h4>
                        <ul className="space-y-3">
                            {companies.map((c) => (
                                <li key={c.slug}>
                                    <button
                                        data-testid={`footer-company-${c.slug}`}
                                        onClick={() => navigate(c.route)}
                                        className="text-sm text-[#a2a59d] hover:text-[#f6c422] transition-colors duration-300 text-left"
                                    >
                                        {c.fullName}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <h4 className="overline-text mb-6">{ui.footer.contact}</h4>
                        <a
                            data-testid="footer-email-link"
                            href={`mailto:${CONTACT.email}`}
                            className="flex items-center gap-2 text-sm text-[#a2a59d] hover:text-[#f6c422] transition-colors duration-300 mb-4"
                        >
                            <Mail className="w-4 h-4 text-[#f6c422]" />
                            {CONTACT.email}
                        </a>
                        <a
                            data-testid="footer-whatsapp-link"
                            href={CONTACT.whatsapp}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-sm text-[#a2a59d] hover:text-[#f6c422] transition-colors duration-300"
                        >
                            <MessageCircle className="w-4 h-4 text-[#f6c422]" />
                            WhatsApp {CONTACT.whatsappLabel}
                        </a>
                    </div>
                </div>

                <div className="mt-16 pt-8 border-t border-[#4a4c42]/60 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                    <p className="text-xs text-[#a2a59d]/70 tracking-wide">
                        {ui.footer.copyright}
                    </p>
                    <button
                        data-testid="footer-back-to-top"
                        onClick={() =>
                            window.__lenis
                                ? window.__lenis.scrollTo(0, { duration: 1.4 })
                                : window.scrollTo({ top: 0, behavior: "smooth" })
                        }
                        className="inline-flex items-center gap-2 text-[11px] font-display font-semibold tracking-[0.2em] uppercase text-[#a2a59d] hover:text-[#f6c422] transition-colors duration-300"
                        aria-label="Back to top"
                    >
                        {ui.footer.backToTop} <ArrowUp className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </footer>
    );
};
